import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import Select from "react-select";
import { AsYouType, parsePhoneNumberFromString, isValidPhoneNumber } from "libphonenumber-js";
import "./AuthTheme.css";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config";
import logo from "../assets/pairfon-logo-horizontal-dark.png";
import PasswordInput from "../components/PasswordInput";

function validateEmail(val) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(val.trim());
}

// Format the typed national number for the selected country as the user types.
function formatNational(value, iso) {
  const digits = (value || "").replace(/[^\d]/g, "");
  if (!iso) return digits;
  try {
    return new AsYouType(iso).input(digits);
  } catch {
    return digits;
  }
}

const step1Rules = {
  company_name: (v) => (!v.trim() ? "Company name is required." : ""),
  address: (v) => (!v.trim() ? "Address is required." : ""),
  city: (v) => (!v.trim() ? "City is required." : ""),
  country: (v) => (!v ? "Please select a country." : ""),
  postal_code: (v) => (!v.trim() ? "Postal code is required." : ""),
};

const step2Rules = {
  first_name: (v) => (!v.trim() ? "First name is required." : ""),
  last_name: (v) => (!v.trim() ? "Last name is required." : ""),
  email: (v) => (!v.trim() ? "Email is required." : !validateEmail(v) ? "Enter a valid email address." : ""),
  phone: (v, fd) => {
    if (!v.trim()) return "Phone number is required.";
    const iso = (fd && fd.phone_country) || "";
    try {
      if (iso && !isValidPhoneNumber(v, iso)) return "Enter a valid phone number.";
    } catch {
      if (v.replace(/\D/g, "").length < 6) return "Enter a valid phone number.";
    }
    return "";
  },
  password: (v) => {
    if (!v) return "Password is required.";
    if (v.length < 8) return "Min 8 characters.";
    if (!/[A-Z]/.test(v)) return "Need an uppercase letter.";
    if (!/[a-z]/.test(v)) return "Need a lowercase letter.";
    if (!/[0-9]/.test(v)) return "Need a number.";
    if (!/[^A-Za-z0-9]/.test(v)) return "Need a special character.";
    return "";
  },
};

function runValidation(rules, formData) {
  const errs = {};
  for (const [field, fn] of Object.entries(rules)) {
    const msg = fn(formData[field] || "", formData);
    if (msg) errs[field] = msg;
  }
  return errs;
}

export default function Signup() {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    company_name: "",
    address: "",
    city: "",
    state: "",
    postal_code: "",
    country: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    phone_country: "US",
    password: "",
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [attempted, setAttempted] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [popupType, setPopupType] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";
  const addressInputRef = useRef(null);
  const autocompleteRef = useRef(null);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...formData, [name]: value };
    if (name === "country" && value) {
      updated.phone_country = value;
      updated.phone = formatNational(updated.phone, value);
    }
    if (name === "phone") {
      updated.phone = formatNational(value, updated.phone_country);
    }
    setFormData(updated);
    if (attempted) {
      const rules = currentStep === 1 ? step1Rules : step2Rules;
      if (rules[name]) {
        const msg = rules[name](updated[name], updated);
        setFieldErrors((prev) => ({ ...prev, [name]: msg }));
      }
    }
  };

  const handleDialChange = (iso) => {
    setFormData((prev) => {
      const next = { ...prev, phone_country: iso, phone: formatNational(prev.phone, iso) };
      if (attempted) {
        const msg = step2Rules.phone(next.phone, next);
        setFieldErrors((pe) => ({ ...pe, phone: msg }));
      }
      return next;
    });
  };

  const extractAddressComponents = (place) => {
    const components = place.address_components || [];
    const getComponent = (types, useShort = false) => {
      const match = components.find((c) => types.some((t) => c.types.includes(t)));
      if (!match) return "";
      return useShort ? match.short_name : match.long_name;
    };
    const streetNumber = getComponent(["street_number"]);
    const route = getComponent(["route"]);
    const city = getComponent(["locality", "postal_town", "administrative_area_level_3", "administrative_area_level_2", "sublocality", "sublocality_level_1"], false);
    const state = getComponent(["administrative_area_level_1"], true);
    const postal = getComponent(["postal_code"]);
    const country = getComponent(["country"], true);
    return {
      address: place.formatted_address || [streetNumber, route].filter(Boolean).join(" "),
      city,
      state,
      postal_code: postal,
      country: country ? country.toUpperCase() : "",
    };
  };

  useEffect(() => {
    if (currentStep !== 1 || !googleMapsApiKey || !addressInputRef.current) return undefined;
    const scriptId = "google-places-sdk";
    const setupAutocomplete = () => {
      if (!window.google?.maps?.places || !addressInputRef.current) return;
      autocompleteRef.current = new window.google.maps.places.Autocomplete(addressInputRef.current, {
        fields: ["address_components", "formatted_address"],
        types: ["geocode"],
      });
      autocompleteRef.current.addListener("place_changed", () => {
        const place = autocompleteRef.current?.getPlace();
        if (!place?.address_components) return;
        const parsed = extractAddressComponents(place);
        const filtered = Object.fromEntries(Object.entries(parsed).filter(([, v]) => Boolean(v)));
        setFormData((prev) => ({ ...prev, ...filtered }));
      });
    };
    if (window.google?.maps?.places) {
      setupAutocomplete();
      return () => { if (autocompleteRef.current && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(autocompleteRef.current); };
    }
    let script = document.getElementById(scriptId);
    if (!script) {
      script = document.createElement("script");
      script.id = scriptId;
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      document.body.appendChild(script);
    }
    script.addEventListener("load", setupAutocomplete);
    return () => {
      script?.removeEventListener("load", setupAutocomplete);
      if (autocompleteRef.current && window.google?.maps?.event) window.google.maps.event.clearInstanceListeners(autocompleteRef.current);
    };
  }, [currentStep, googleMapsApiKey]);

  useEffect(() => {
    const loadCountries = async () => {
      try {
        const { data } = await axios.get(`${API_BASE_URL}/geo/countries`);
        setCountries(data || []);
      } catch (error) {
        console.error("Unable to load countries", error);
      }
    };
    loadCountries();
  }, []);

  useEffect(() => {
    const loadStates = async () => {
      if (!formData.country) { setStates([]); return; }
      try {
        const { data } = await axios.get(`${API_BASE_URL}/geo/states?country=${encodeURIComponent(formData.country)}`);
        setStates(data || []);
      } catch {
        setStates([]);
      }
    };
    loadStates();
  }, [formData.country]);

  useEffect(() => {
    const token = localStorage.getItem("userToken");
    if (token) navigate("/dashboard");
  }, [navigate]);

  const handleNext = (e) => {
    e.preventDefault();
    setAttempted(true);
    const errs = runValidation(step1Rules, formData);
    if (!states.length) delete errs.state;
    setFieldErrors(errs);
    if (Object.keys(errs).length === 0) {
      setAttempted(false);
      setFieldErrors({});
      setCurrentStep(2);
    }
  };

  const handlePrevious = (e) => {
    e.preventDefault();
    setAttempted(false);
    setFieldErrors({});
    setCurrentStep(1);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAttempted(true);
    const errs = runValidation(step2Rules, formData);
    setFieldErrors(errs);
    if (Object.keys(errs).length > 0) return;

    let e164 = "";
    try {
      const parsed = parsePhoneNumberFromString(formData.phone, formData.phone_country);
      if (parsed && parsed.isValid()) e164 = parsed.number;
    } catch {
      e164 = "";
    }
    if (!e164) {
      const dial = (countries.find((c) => c.code === formData.phone_country)?.phonecode || "").replace(/\D/g, "");
      e164 = `+${dial}${formData.phone.replace(/\D/g, "")}`;
    }

    setSubmitting(true);
    setErrorMessage("");
    try {
      const response = await axios.post(`${API_BASE_URL}/register`, {
        company: {
          company_name: formData.company_name,
          address: formData.address,
          city: formData.city,
          state: formData.state,
          postal_code: formData.postal_code,
          country: formData.country,
        },
        owner: {
          first_name: formData.first_name,
          last_name: formData.last_name,
          email: formData.email,
          phone: e164,
          password: formData.password,
        },
      });
      if (response.status === 201) {
        setPopupType("success");
        setShowPopup(true);
        setTimeout(() => navigate("/login"), 3000);
      }
    } catch (error) {
      setPopupType("error");
      setShowPopup(true);
      setErrorMessage(error.response?.data?.error || "Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const f = (name) => ({
    name,
    value: formData[name],
    onChange: handleChange,
    className: `pf-input${fieldErrors[name] ? " pf-invalid" : ""}`,
  });

  const dialCode = (pc) => String(pc || "").replace(/^\+/, "");
  const dialOptions = countries
    .filter((c) => c.phonecode)
    .map((c) => ({ value: c.code, phonecode: c.phonecode, name: c.name, label: `${c.name} +${dialCode(c.phonecode)}` }));
  const selectedDial = dialOptions.find((o) => o.value === formData.phone_country) || null;

  // react-select styled to match the dark inputs.
  const dialStyles = {
    container: (b) => ({ ...b, flex: "0 0 118px" }),
    control: (b, state) => ({
      ...b, minHeight: 46, borderRadius: 10, borderWidth: 1, boxShadow: "none",
      backgroundColor: "#0a0c12",
      borderColor: state.isFocused ? "#7c7cff" : "rgba(255,255,255,.11)",
      "&:hover": { borderColor: state.isFocused ? "#7c7cff" : "rgba(255,255,255,.11)" },
    }),
    valueContainer: (b) => ({ ...b, padding: "2px 10px" }),
    singleValue: (b) => ({ ...b, color: "#eef0f6", fontSize: 14 }),
    placeholder: (b) => ({ ...b, color: "#565b70", fontSize: 14 }),
    input: (b) => ({ ...b, color: "#eef0f6" }),
    indicatorSeparator: () => ({ display: "none" }),
    dropdownIndicator: (b) => ({ ...b, padding: 6, color: "#8b8fa3" }),
    menu: (b) => ({ ...b, backgroundColor: "#0d0f16", border: "1px solid rgba(255,255,255,.1)", overflow: "hidden", zIndex: 30 }),
    menuPortal: (b) => ({ ...b, zIndex: 9999 }),
    option: (b, state) => ({
      ...b, fontSize: 14, color: "#eef0f6", cursor: "pointer",
      backgroundColor: state.isSelected ? "#5b54e6" : state.isFocused ? "rgba(124,124,255,.14)" : "transparent",
    }),
  };

  return (
    <div className="pf-auth">
      <div className="pf-shell">
        <aside className="pf-hero">
          <img className="pf-logo" src={logo} alt="Pairfon" />
          <div className="pf-hero-body">
            <span className="pf-badge">SMS · WhatsApp · iMessage</span>
            <h2>Start texting from<br />every channel.</h2>
            <p>
              One account for <b style={{ color: "#a5a8ff" }}>SMS</b>, <b style={{ color: "#25d366" }}>WhatsApp</b> and{" "}
              <b style={{ color: "#0a84ff" }}>iMessage</b> — connected to your CRM in minutes, with your real numbers and no per-message fees.
            </p>
          </div>
          <div className="pf-stats">
            <div><div className="n">3</div><div className="l">Channels, one number</div></div>
            <div><div className="n">$0</div><div className="l">Per-message fees</div></div>
            <div><div className="n">2 min</div><div className="l">Setup time</div></div>
          </div>
        </aside>

        <div className="pf-card">
          <div>
            <span className="pf-badge">{currentStep === 1 ? "Step 1 of 2 · Company" : "Step 2 of 2 · Owner details"}</span>
          </div>
          <div>
            <h1 className="pf-title">Create your account</h1>
            <p className="pf-sub">{currentStep === 1 ? "Tell us about your company." : "Now set up the owner / admin login."}</p>
          </div>

          {currentStep === 1 && (
            <form className="pf-form" noValidate>
              <div className="pf-field">
                <label>Company name</label>
                <input type="text" placeholder="Acme Inc." {...f("company_name")} />
                {fieldErrors.company_name && <span className="pf-err">{fieldErrors.company_name}</span>}
              </div>
              <div className="pf-field">
                <label>Address</label>
                <input type="text" placeholder="Street address" {...f("address")} ref={addressInputRef} />
                {fieldErrors.address && <span className="pf-err">{fieldErrors.address}</span>}
              </div>
              <div className="pf-row">
                <div className="pf-field">
                  <label>City</label>
                  <input type="text" placeholder="City" {...f("city")} />
                  {fieldErrors.city && <span className="pf-err">{fieldErrors.city}</span>}
                </div>
                <div className="pf-field">
                  <label>State / Province</label>
                  <select {...f("state")} disabled={!states.length}>
                    <option value="">Select state</option>
                    {states.map((s) => (<option key={s.code || s.name} value={s.code || s.name}>{s.name}</option>))}
                  </select>
                  {fieldErrors.state && <span className="pf-err">{fieldErrors.state}</span>}
                </div>
              </div>
              <div className="pf-row">
                <div className="pf-field">
                  <label>Postal code</label>
                  <input type="text" placeholder="Postal code" {...f("postal_code")} />
                  {fieldErrors.postal_code && <span className="pf-err">{fieldErrors.postal_code}</span>}
                </div>
                <div className="pf-field">
                  <label>Country</label>
                  <select {...f("country")}>
                    <option value="">Select country</option>
                    {countries.map((c) => (<option key={c.code || c.name} value={c.code || c.name}>{c.name}</option>))}
                  </select>
                  {fieldErrors.country && <span className="pf-err">{fieldErrors.country}</span>}
                </div>
              </div>
              <button type="submit" className="pf-btn" onClick={handleNext}>Continue</button>
            </form>
          )}

          {currentStep === 2 && (
            <form className="pf-form" noValidate>
              <div className="pf-row">
                <div className="pf-field">
                  <label>First name</label>
                  <input type="text" placeholder="First name" {...f("first_name")} />
                  {fieldErrors.first_name && <span className="pf-err">{fieldErrors.first_name}</span>}
                </div>
                <div className="pf-field">
                  <label>Last name</label>
                  <input type="text" placeholder="Last name" {...f("last_name")} />
                  {fieldErrors.last_name && <span className="pf-err">{fieldErrors.last_name}</span>}
                </div>
              </div>
              <div className="pf-field">
                <label>Email</label>
                <input type="email" placeholder="you@company.com" {...f("email")} />
                {fieldErrors.email && <span className="pf-err">{fieldErrors.email}</span>}
              </div>
              <div className="pf-field">
                <label>Phone number</label>
                <div className="pf-phone">
                  <Select
                    inputId="phone_country"
                    classNamePrefix="dial"
                    placeholder="Code"
                    options={dialOptions}
                    value={selectedDial}
                    onChange={(opt) => handleDialChange(opt ? opt.value : "")}
                    formatOptionLabel={(d, meta) =>
                      meta.context === "menu" ? `${d.name}  +${dialCode(d.phonecode)}` : `${d.value} +${dialCode(d.phonecode)}`
                    }
                    menuPortalTarget={typeof document !== "undefined" ? document.body : null}
                    menuPosition="fixed"
                    filterOption={(opt, input) => {
                      const q = (input || "").toLowerCase();
                      if (!q) return true;
                      const d = opt.data;
                      return d.name.toLowerCase().includes(q) || String(d.phonecode).includes(q.replace("+", "")) || d.value.toLowerCase() === q;
                    }}
                    styles={dialStyles}
                  />
                  <input type="tel" placeholder="Phone number" {...f("phone")} />
                </div>
                {fieldErrors.phone && <span className="pf-err">{fieldErrors.phone}</span>}
              </div>
              <div className="pf-field">
                <label>Password</label>
                <PasswordInput
                  name="password"
                  placeholder="Min 8 characters"
                  value={formData.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  className={`pf-input${fieldErrors.password ? " pf-invalid" : ""}`}
                />
                {fieldErrors.password && <span className="pf-err">{fieldErrors.password}</span>}
              </div>
              <div className="pf-btn-row">
                <button type="button" className="pf-btn pf-btn--ghost" onClick={handlePrevious}>Back</button>
                <button type="submit" className="pf-btn" onClick={handleSubmit} disabled={submitting}>
                  {submitting ? "Submitting…" : "Create account"}
                </button>
              </div>
            </form>
          )}

          <p className="pf-foot">
            Already have an account?{" "}
            <button type="button" className="pf-link" onClick={() => navigate("/login")}>Sign in</button>
          </p>
        </div>
      </div>

      {showPopup && (
        <div className="pf-popup">
          <div className={`pf-popup-card ${popupType === "success" ? "ok" : "err"}`}>
            {popupType === "success" ? (
              <>
                <h2>Account created 🎉</h2>
                <p>Please verify your email to complete registration, then choose a plan.</p>
                <div className="pf-popup-actions">
                  <button className="pf-btn" onClick={() => { setShowPopup(false); navigate("/login"); }}>Continue</button>
                </div>
              </>
            ) : (
              <>
                <h2>Something went wrong</h2>
                <p>{errorMessage}</p>
                <div className="pf-popup-actions">
                  <button className="pf-btn pf-btn--ghost" onClick={() => setShowPopup(false)}>Try again</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
